<template>
    <form
        class="guideline-search"
        :class="{
            'guideline-search--active': !!searchQuery
        }"
        @submit.prevent="doSearch"
    >
        <div class="guideline-search__container">
            <div class="guideline-search__input">
                <BaseInput
                    :model-value="searchQuery"
                    name="search"
                    placeholder="Search"
                    :wrapper-attrs="{
                        class: 'dn-input--rounded'
                    }"
                    :clearable="false"
                    @update:model-value="searchQuery = $event.target.value"
                >
                    <template #after>
                        <transition
                            name="fade"
                            mode="out-in"
                        >
                            <button
                                v-if="searchQuery"
                                key="close"
                                type="button"
                                aria-label="Clear input"
                                @click="searchQuery = ''"
                            >
                                <BaseIcon
                                    icon="close"
                                    data-icon="close"
                                />
                            </button>

                            <button
                                v-else
                                key="search"
                                type="button"
                                class="button--clean"
                                aria-label="Search"
                            >
                                <BaseIcon
                                    icon="search"
                                    data-icon="search"
                                />
                            </button>
                        </transition>
                    </template>
                </BaseInput>
            </div>

            <div
                v-show="searchQuery"
                class="guideline-search__button"
            >
                <BaseButton
                    type="button"
                    class="base-button--icon"
                    title="Show previous result"
                    :disabled="!isPreviousButtonEnabled"
                    aria-label="Go to previous highlight"
                    @click="goToPreviousHighlight"
                >
                    <BaseIcon icon="chevron-up" />
                </BaseButton>
            </div>

            <div
                v-show="searchQuery"
                class="guideline-search__button"
            >
                <BaseButton
                    type="button"
                    class="base-button--icon"
                    title="Show next result"
                    :disabled="!isNextButtonEnabled"
                    aria-label="Go to next highlight"
                    @click="goToNextHighlight"
                >
                    <BaseIcon icon="chevron-down" />
                </BaseButton>
            </div>

            <ClientOnly>
                <div
                    v-show="searchQuery"
                    class="guideline-search__count"
                >
                    <template v-if="isSearching">
                        Searching...
                    </template>
                    <template v-else-if="totalHighlights === 1">
                        1 result
                    </template>
                    <template v-else-if="totalHighlights > 1">
                        {{ currentHighlightNumber }} / {{ totalHighlights }} results
                    </template>
                    <template v-else>
                        No results found
                    </template>
                </div>
                <template #fallback>
                    <div
                        v-show="searchQuery"
                        class="guideline-search__count"
                    >
                        Searching...
                    </div>
                </template>
            </ClientOnly>
        </div>
    </form>
</template>

<script setup>
import {debounce} from 'lodash-es';
import {useGuidelineSearchStore} from '~/store/guideline-search';
import SearchGuidelineQuery from '~/graphql/queries/guidelines/search.graphql';
import {storeToRefs} from 'pinia';

const router = useRouter();

const {
    chapters,
    searchQuery,
    totalHighlights,
    activeSearch,
    currentHighlightNumber,
    // currentChapterContent,
    chaptersWithHighlights,
    nextChaptersWithHighlights,
    highlightOffset,
    previousChaptersWithHighlights,
    // currentChapter,
    isFirstHighlightInChapter,
    isLastHighlightInChapter,
} = storeToRefs(useGuidelineSearchStore());


const props = defineProps({
    guideline: {
        type: Object,
        required: true,
        default: null
    }
});

const isPreviousButtonEnabled = computed(() => {
    if (isSearching.value) {
        return false;
    }

    return currentHighlightNumber.value > 1;
});

const isNextButtonEnabled = computed(() => {
    if (isSearching.value) {
        return false;
    }

    return currentHighlightNumber.value < totalHighlights.value;
});

const route = useRoute();
const searchValue = ref('');

const { activeSite } = useMultisite();
const { variables } = useCraftGraphql();
delete variables.uri;

watchEffect(() => {
    if(searchValue?.value?.length >= 3) {
        variables.search = searchValue;
    }
    variables.guidelineId = props.guideline?.id;
    variables.chapterSlug = route.params?.chapterSlug || '';

    if (activeSite?.value?.handle) {
        variables.site = activeSite.value?.handle || null;
    }
});

watch(searchQuery, debounce(() => {
    if (searchQuery.value === '') {
        reset();
        return;
    }

    doSearch();
}, 500));

const skipSearch = computed(() => {
    // use variables to check if search is neccessary.
    return !variables?.search || variables?.search?.length < 3 || !props.guideline?.id;
});

const { data, pending: isSearching } = await useAsyncQuery({
    query: SearchGuidelineQuery,
    variables,
    skip: skipSearch
});

function reset() {
    useGuidelineSearchStore().$patch({
        isSearching: false,
        searchQuery: null,
        totalHighlights: null,
        currentHighlightNumber: 0,
        chapters: [],
        currentChapterContent: null
    });
}

function nextHighlight() {
    if (currentHighlightNumber.value === totalHighlights.value) {
        return;
    }

    useGuidelineSearchStore().$patch({
        currentHighlightNumber: currentHighlightNumber.value + 1,
    });
}

function previousHighlight() {
    if (currentHighlightNumber.value === 1) {
        return;
    }

    useGuidelineSearchStore().$patch({
        currentHighlightNumber: currentHighlightNumber.value - 1
    });
}

const searchResult = computed(() => {
    return data?.value?.results?.[0];
});

watch(searchResult, (result) => {
    // Format chapter meta data
    const mappedChapters = result?.chapters?.map((chapter) => {
        return {
            slug: chapter.slug,
            id: chapter.id,
            highlightCount: chapter.chapterMatchesCount
        };
    });

    if (!mappedChapters?.length) {
        return;
    }

    useGuidelineSearchStore().$patch({
        totalHighlights: result?.totalMatchesCount,
        chapters: mappedChapters
    });

    const currentChapter = result?.chapters?.find((chapter) => chapter.slug === variables.chapterSlug);

    if (!variables.chapterSlug) {
        // When no slug is provided, we're working with the first chapter.
        useGuidelineSearchStore().$patch({
            currentChapterContent: result?.chapters?.[0]?.chapterContent,
            currentHighlightNumber: 1,
            // activeSearch: searchValue?.value
        });
    } else {
        // If, however, a slug is available: set that chapter's content
        useGuidelineSearchStore().$patch({
            currentChapterContent: currentChapter?.chapterContent,
            currentChapterSlug: variables.chapterSlug,
        });

        useGuidelineSearchStore().$patch({
            currentHighlightNumber: highlightOffset.value + 1
        });

        // Go to the first highlight of the chapter if not defined or out of range
        if (
            !currentHighlightNumber.value ||
            currentHighlightNumber.value < highlightOffset.value + 1 ||
            currentHighlightNumber.value > highlightOffset.value + currentChapter.highlightCount
        ) {
            currentHighlightNumber.value = highlightOffset.value + 1;
        }
    }
});

async function doSearch() {
    searchValue.value = searchQuery?.value;

    if (searchResult?.value === null || searchResult?.value?.length < 3) {
        return;
    }

    if (!searchQuery?.value || !props.guideline?.id) {
        // reset store
        reset();
        return;
    }

    // Check if current search matches active search. If so, we assume the user intended to skip to the next result.
    if (searchQuery.value === activeSearch.value) {
        goToNextHighlight();
        return;
    }

    // Wait for chaptersWithHighlights to be populated
    await new Promise((resolve) => {
        const unwatch = watch(chaptersWithHighlights, (value) => {
            if (value && value.length > 0) {
                unwatch();
                resolve(value);
            }
        });
    });

    // Navigate to first chapter containing results
    if (chaptersWithHighlights.value && chaptersWithHighlights.value.length > 0) {
        navigateToChapter(chaptersWithHighlights?.value?.[0]);
    }

    activeSearch.value = searchQuery?.value;
}

function navigateToChapter(chapter) {
    variables.chapterSlug = chapter.slug;

    useGuidelineSearchStore().$patch({
        currentChapterSlug: chapter.slug
    });

    router.push({
        name: 'guideline-chapterSlug',
        params: {
            slug: props.guideline.slug,
            chapterSlug: chapter.slug
        }
    }).catch(() => {
        // I know it's empty, blame this guy:
        // https://github.com/vuejs/vue-router/issues/2872#issuecomment-519073998
    });
}

function goToNextHighlight() {
    const nextChapter = nextChaptersWithHighlights?.value?.[0];

    if (isLastHighlightInChapter.value && nextChapter) {
        // Increase pointer before navigating
        nextHighlight();
        navigateToChapter(nextChapter);
        return;
    }

    nextHighlight();
}

function goToPreviousHighlight() {
    const previousChapter = previousChaptersWithHighlights?.value?.[previousChaptersWithHighlights?.value?.length - 1];

    if (isFirstHighlightInChapter.value && previousChapter) {
        // Decrease pointer before navigating
        previousHighlight();
        navigateToChapter(previousChapter);
        return;
    }

    previousHighlight();
}

watch(() => route.params.chapterSlug, () => {
    variables.chapterSlug = route.params.chapterSlug;
    variables.search = activeSearch.value;

    const targetChapter = chapters.value.find((chapter) => chapter.slug === route.params.chapterSlug);

    if (!targetChapter) {
        return;
    }

    navigateToChapter(targetChapter);
});
</script>

<style lang="less">
.guideline-search {
    .grid-bleed;
    .grid-container;

    z-index: 1;

    padding: var(--spacing-2xl) 0 var(--spacing-sm) 0;
    background: var(--color-lightest);
    transition: background var(--transition-fast), padding var(--transition-fast);

    @media @q-md-min {
        top: 4.875rem;
    }
}

.guideline-search__container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.guideline-search__input {
    flex: 1 1 auto;
    margin-right: .25rem;

    .dn-input__after .dn-icon {
        color: var(--color-darkest);

        &:hover {
            color: var(--color-primary);
        }
    }
}

.guideline-search__button,
.guideline-search__count {
    margin-left: .875rem;
}

.guideline-search__count {
    .typography-label-secondary;
    display: flex;
    align-items: center;

    color: var(--color-lightest);
    line-height: 1;

    @media @q-sm-max {
        width: 100%;
        flex-basis: 100%;
        margin-left: 0;
        margin-top: var(--spacing-md);
    }
}

.guideline-search--active {
    position: sticky;
    top: 3.5rem;
    margin-top: 2rem;
    background: var(--color-darkest);
    z-index: 3;
    padding: var(--spacing-lg) 0;

    @media @q-md-min {
        top: 4.5rem;
        padding: var(--spacing-2xl) 0;
    }

    @media @q-sm-max {
        .guideline-search__input {
            max-width: calc(100% - 8rem);
        }
    }
}
</style>
